import { useState, useEffect } from 'react';
import AnimatedSection from '../../../components/base/AnimatedSection';

const features = [
  {
    icon: 'ri-shield-check-line',
    title: 'Kanıtlanmış Başarı',
    description: '500+ otel işletmesinin gelirini ortalama %35 artırdık. Veriye dayalı stratejilerimiz, sektörde benchmark oluşturuyor.',
    color: 'from-gold-400 to-gold-500',
  },
  {
    icon: 'ri-customer-service-2-line',
    title: 'Online Destek',
    description: 'Uzman ekibimiz her an yanınızda. Kurulum, eğitim ve sürekli optimizasyon desteği ile işletmenizi yalnız bırakmıyoruz.',
    color: 'from-primary to-primary/80',
  },
  {
    icon: 'ri-line-chart-line',
    title: 'Gerçek Zamanlı Analitik',
    description: 'Tüm kanallarınızı tek panelden yönetin. Anlık raporlar, tahmine dayalı analizler ve aksiyon önerileri.',
    color: 'from-gold-500 to-gold-600',
  },
  {
    icon: 'ri-rocket-line',
    title: 'Hızlı Kurulum',
    description: '2-4 hafta içinde sisteminiz hazır. Minimum aksama, maksimum verimlilik. Mevcut sistemlerinizle sorunsuz entegrasyon.',
    color: 'from-gold-400 to-gold-500',
  },
  {
    icon: 'ri-lock-2-line',
    title: 'Güvenli Altyapı',
    description: 'Verileriniz SSL şifrelemesi ve ISO 27001 standartlarıyla korunuyor. KVKK ve GDPR uyumlu sistemler.',
    color: 'from-primary to-primary/80',
  },
  {
    icon: 'ri-lightbulb-flash-line',
    title: 'Sürekli İnovasyon',
    description: 'AI destekli fiyatlama, otomatik overbooking, dinamik kanal yönetimi. Teknoloji her gün gelişiyor.',
    color: 'from-gold-500 to-gold-600',
  },
];

const metrics = [
  { value: '500+', label: 'Mutlu Otel', icon: 'ri-hotel-line' },
  { value: '%35', label: 'Ortalama Gelir Artışı', icon: 'ri-line-chart-line' },
  { value: '₺180M+', label: 'Toplam Ek Gelir', icon: 'ri-money-dollar-circle-line' },
  { value: '7/24', label: 'AI ile Kesintisiz Destek', icon: 'ri-customer-service-2-line' },
];

export default function TrustSection() {
  const [counters, setCounters] = useState([0, 0, 0, 0]);

  // Animated Counters
  useEffect(() => {
    const duration = 2000;
    const steps = 60;
    const targets = [500, 35, 180, 24];
    let currentStep = 0;

    const interval = setInterval(() => {
      currentStep++;
      if (currentStep <= steps) {
        setCounters(targets.map(target => Math.floor((target * currentStep) / steps)));
      } else {
        clearInterval(interval);
        setCounters(targets);
      }
    }, duration / steps);

    return () => clearInterval(interval);
  }, []);

  return (
    <section className="relative py-32 bg-gradient-to-b from-white via-primary/5 to-white overflow-hidden">
      {/* Decorative Background */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 left-0 w-full h-full bg-[radial-gradient(circle_at_30%_20%,rgba(212,175,55,0.1),transparent_50%)]"></div>
        <div className="absolute bottom-0 right-0 w-full h-full bg-[radial-gradient(circle_at_70%_80%,rgba(11,29,58,0.05),transparent_50%)]"></div>
      </div>

      <div className="max-w-7xl mx-auto px-6 relative z-10">
        {/* Section Header */}
        <AnimatedSection animation="fadeUp">
          <div className="text-center mb-20">
            <span className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-gold-100 to-gold-50 text-gold-700 rounded-full text-sm font-semibold mb-6 border border-gold-200">
              <i className="ri-star-line text-lg"></i>
              Güven ve Başarı
            </span>
            <h2 className="text-4xl md:text-5xl font-bold text-primary mb-6 leading-tight">
              Neden{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-gold-500 to-gold-600">
                Bizi Tercih Ediyorlar?
              </span>
            </h2>
            <p className="text-gray-600 text-lg max-w-3xl mx-auto leading-relaxed">
              Türkiye'nin önde gelen otel zincirleri ve bağımsız otelleri, gelir optimizasyonu için Prime Sales Pro'yu tercih ediyor.
            </p>
          </div>
        </AnimatedSection>

        {/* Features Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-24">
          {features.map((feature, index) => (
            <AnimatedSection key={index} animation="fadeUp" delay={index * 100}>
              <div className="group bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-500 border border-gray-100 hover:border-gold-300 h-full">
                <div
                  className={`w-16 h-16 flex items-center justify-center rounded-xl bg-gradient-to-r ${feature.color} text-white mb-6 group-hover:scale-110 transition-transform duration-300 shadow-lg`}
                >
                  <i className={`${feature.icon} text-3xl`}></i>
                </div>
                <h3 className="text-xl font-bold text-primary mb-4 group-hover:text-gold-600 transition-colors">
                  {feature.title}
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  {feature.description}
                </p>
              </div>
            </AnimatedSection>
          ))}
        </div>

        {/* Success Metrics */}
        <AnimatedSection animation="fadeUp">
          <div className="bg-gradient-to-br from-gold-50 to-white rounded-3xl p-12 md:p-16 border border-gold-200 shadow-xl">
            <div className="text-center mb-12">
              <h3 className="text-3xl md:text-4xl font-bold text-primary mb-4">
                Rakamlarla <span className="text-gold-600">Başarımız</span>
              </h3>
              <p className="text-gray-600 text-lg">
                Veriye dayalı sonuçlar, ölçülebilir başarı
              </p>
            </div>

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
              {metrics.map((metric, index) => (
                <div key={index} className="text-center group">
                  <div className="w-20 h-20 flex items-center justify-center rounded-2xl bg-gradient-to-br from-gold-400 to-gold-500 text-white mx-auto mb-4 shadow-lg group-hover:scale-110 transition-transform duration-300">
                    <i className={`${metric.icon} text-3xl`}></i>
                  </div>
                  <p className="text-4xl md:text-5xl font-bold text-primary mb-2">
                    {index === 0 && `${counters[0]}+`}
                    {index === 1 && `%${counters[1]}`}
                    {index === 2 && `₺${counters[2]}M+`}
                    {index === 3 && `7/${counters[3]}`}
                  </p>
                  <p className="text-gray-600 font-medium">{metric.label}</p>
                </div>
              ))}
            </div>
          </div>
        </AnimatedSection>
      </div>
    </section>
  );
}
